import { useQuery } from "@tanstack/react-query";
import { DateRange } from "react-day-picker";
import { queryKeys } from "@/queryKeys";
import { borrowerService } from "@/services/borrowers";
import { BorrowerFilters } from "@/types/borrower";
import { WORKFLOW_BUCKETS } from "@/components/borrower/workflow/workflowBuckets";

export function useBorrowersQuery(
  filters: BorrowerFilters = {},
  page: number,
  pageSize = 20,
  enabled = true
) {
  return useQuery({
    queryKey: queryKeys.borrowers(filters as Record<string, unknown>, page, pageSize),
    queryFn: () => borrowerService.list({ ...filters, page, page_size: pageSize }),
    enabled,
    refetchInterval: 60_000,
  });
}

// Only the statuses the board actually renders, so the server returns a small,
// focused result set instead of every active application in the org.
const WORKFLOW_STATUSES = WORKFLOW_BUCKETS.flatMap((bucket) => bucket.statuses).join(",");

/** Formats a Date as the local-calendar-day `YYYY-MM-DD` the API expects. */
function toLocalDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Builds the server-side `created_after`/`created_before` params from a range. */
function dateRangeParams(
  dateRange?: DateRange
): Pick<BorrowerFilters, "created_after" | "created_before"> {
  const params: Pick<BorrowerFilters, "created_after" | "created_before"> = {};
  if (dateRange?.from) params.created_after = toLocalDateParam(dateRange.from);
  if (dateRange?.to) params.created_before = toLocalDateParam(dateRange.to);
  return params;
}

/**
 * Fetches the applications for the workflow board.
 *
 * Filtering (workflow statuses + a `created_at` date range) is pushed to the
 * server via `?view=workflow`, which returns the (small, pre-filtered) result
 * set unpaginated in a single request. The board then groups the results into
 * status buckets.
 */
export function useWorkflowAppsQuery(
  filters: BorrowerFilters = {},
  dateRange?: DateRange,
  enabled = true
) {
  const workflowFilters: BorrowerFilters = {
    ...filters,
    app_status__in: WORKFLOW_STATUSES,
    view: "workflow",
    ...dateRangeParams(dateRange),
  };

  return useQuery({
    queryKey: [
      ...queryKeys.borrowers(workflowFilters as Record<string, unknown>),
      "workflow",
    ] as const,
    queryFn: () => borrowerService.listWorkflow(workflowFilters),
    enabled,
    // Board data (esp. the "contract viewed" eye indicator) changes from
    // external events (merchant opens/signs a DocuSeal doc) that nothing on
    // this page triggers directly, so poll + refresh on refocus to keep it
    // reasonably live instead of requiring a manual page reload.
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
